import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { getApiErrorMessage } from '@/api/api.ts'
import { apiStartTestSession } from '@/api/user/startTestSession.ts'
import { apiEndTestSession } from '@/api/user/endTestSession.ts'
import { invalidateTestSessionQueries } from '@/api/user/getActiveTestSessions.ts'
import TestModeToast from '@/component/testMode/TestModeToast.tsx'

export interface ITestSessionTarget {
    id: number
    protecteeId: number
    protecteeName: string
}

const useTestSessionActions = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const [busyProtecteeIds, setBusyProtecteeIds] = useState<number[]>([])

    const markBusy = (ids: number[], busy: boolean) =>
        setBusyProtecteeIds(current =>
            busy ? [...current, ...ids] : current.filter(id => !ids.includes(id))
        )

    const isBusy = (protecteeId: number) => busyProtecteeIds.includes(protecteeId)

    const start = async (protecteeId: number) => {
        markBusy([protecteeId], true)
        try {
            await apiStartTestSession(protecteeId)
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('testMode.toast.startFail')))
        } finally {
            await invalidateTestSessionQueries(queryClient)
            markBusy([protecteeId], false)
        }
    }

    const end = async (target: ITestSessionTarget) => {
        markBusy([target.protecteeId], true)
        try {
            await apiEndTestSession(target.protecteeId, target.id)
            toast.success(
                <TestModeToast
                    title={t('testMode.toast.ended.title')}
                    description={t('testMode.toast.ended.desc', { name: target.protecteeName })}
                />
            )
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('testMode.toast.endFail')))
        } finally {
            await invalidateTestSessionQueries(queryClient)
            markBusy([target.protecteeId], false)
        }
    }

    const endAll = async (targets: ITestSessionTarget[]) => {
        if (targets.length === 0) return
        const ids = targets.map(target => target.protecteeId)
        markBusy(ids, true)
        try {
            const results = await Promise.allSettled(
                targets.map(target => apiEndTestSession(target.protecteeId, target.id))
            )
            const failed = results.filter(result => result.status === 'rejected').length
            if (failed > 0) {
                toast.error(t('testMode.toast.endAllFail', { count: failed }))
            } else {
                toast.success(
                    <TestModeToast
                        title={t('testMode.toast.ended.title')}
                        description={t('testMode.panel.footer')}
                    />
                )
            }
        } finally {
            await invalidateTestSessionQueries(queryClient)
            markBusy(ids, false)
        }
    }

    return { start, end, endAll, isBusy }
}

export default useTestSessionActions
